import type { WorkspaceMember } from '@easy-expense/data-schema-v2';

/**
 * The QA Pool is a fixed, ordered set of addresses reserved for filling staging workspaces with
 * disposable members. It is shared across all of staging and exists independently of any one
 * workspace -- a Pool Account only becomes a WorkspaceMember once it is invited.
 */
const POOL_LOCAL_PART = 'eeqateam';
const POOL_DOMAIN = 'gmail.com';

/**
 * Canonical Pool Account addresses only: a 1-based index with no leading zeros, so `+1` and `+01`
 * can never both be treated as index 1 and fight over the same slot.
 */
const POOL_EMAIL_PATTERN = /^eeqateam\+([1-9]\d*)@gmail\.com$/;

/**
 * Beyond this the sequential cold run takes minutes and the pool stops being worth the pollution.
 */
export const MAX_POOL_SIZE = 50;

/** The Pool Account address at a 1-based index. */
export function qaPoolEmail(index: number): string {
  return `${POOL_LOCAL_PART}+${index}@${POOL_DOMAIN}`;
}

/** The 1-based Pool Account index for an address, or null if it is not a Pool Account at all. */
export function qaPoolIndex(email: string | null | undefined): number | null {
  const match = email?.trim().toLowerCase().match(POOL_EMAIL_PATTERN);
  if (!match) {
    return null;
  }
  return Number(match[1]);
}

/**
 * The single guard on the destructive path -- both directions of reconciliation route through this,
 * so a real teammate can never be mistaken for a Pool Account.
 */
export function isQaPoolEmail(email: string | null | undefined): boolean {
  return qaPoolIndex(email) !== null;
}

/** Constrains a requested Pool Size to a whole number within the supported range. */
export function clampPoolSize(poolSize: number): number {
  if (!Number.isFinite(poolSize)) {
    return 0;
  }
  return Math.min(Math.max(Math.floor(poolSize), 0), MAX_POOL_SIZE);
}

/** Counts the Pool Accounts currently holding active membership in a workspace. */
export function countPoolMembers(members: WorkspaceMember[]): number {
  return activePoolMembersByIndex(members).size;
}

function activePoolMembersByIndex(members: WorkspaceMember[]): Map<number, WorkspaceMember> {
  const poolMembers = new Map<number, WorkspaceMember>();
  for (const member of members) {
    if (member.entityState !== 'active') {
      continue;
    }
    const index = qaPoolIndex(member.email);
    if (index !== null && !poolMembers.has(index)) {
      poolMembers.set(index, member);
    }
  }
  return poolMembers;
}

export type PoolReconciliation = {
  toInvite: string[];
  toArchive: WorkspaceMember[];
};

/**
 * Works out what it would take for exactly Pool Accounts 1..poolSize to hold active membership in
 * this workspace. Reconciles in both directions, so a lower Pool Size archives the Pool Accounts
 * above it and a Pool Size of zero tears the pool out of the workspace entirely.
 *
 * Only ever reports Pool Accounts -- the signed-in user and real teammates are invisible to it.
 *
 * The workspace owner is exempt from removal even when they are a Pool Account, which is the normal
 * case on staging: QA signs in as a Pool Account and creates the workspace with it. Archiving the
 * owner would strand the workspace, so they keep their slot instead -- occupied, so no redundant
 * re-invite, but never archived, whatever the Pool Size.
 *
 * @param members every workspace member, active or not
 * @param poolSize how many Pool Accounts should end up active in this workspace
 * @param ownerUserID the workspace's `createdBy`, the one member reconciliation may not remove
 */
export function planPoolReconciliation(
  members: WorkspaceMember[],
  poolSize: number,
  ownerUserID?: string | null,
): PoolReconciliation {
  const target = clampPoolSize(poolSize);
  const poolMembers = activePoolMembersByIndex(members);

  const toInvite: string[] = [];
  for (let index = 1; index <= target; index++) {
    if (!poolMembers.has(index)) {
      toInvite.push(qaPoolEmail(index));
    }
  }

  const toArchive = [...poolMembers.entries()]
    .filter(([index, member]) => index > target && member.userID !== ownerUserID)
    .sort(([left], [right]) => left - right)
    .map(([, member]) => member);

  return { toInvite, toArchive };
}
